/**
 * MandateWizardDialog - the E6 mandate wizard, extended for P4-S1 (steps 1-6).
 *
 * WHAT IT IS, in the words of the requirement (build plan PART 2B, E6 confirmed detail):
 * "the wizard is a centered popup over a blurred page; the user never leaves the current
 * page; inside it they review + inline-edit the email, review the PDF/Word, delete the
 * attachment, download the Word, fix typos, re-upload as PDF, then send; on send the email
 * goes out, the wizard closes, the status changes, everything is locked."
 *
 * E2 is the other half: NO "are you sure" dialog before it opens. Picking the status IS the
 * trigger, and the SEND is the confirmation. Cancel rolls back to the original status with
 * no PDF left behind.
 *
 * P4-S1 ADDS (concept 7.1 steps 3-6):
 *   - step 3 Unterzeichnende: for a FIRMA the wizard first shows the Handelsregister signatory
 *     proposal (pre-checked); "Weiter" prepares with exactly the checked signers. A natural
 *     person signs for themselves, so no signer step is shown and prepare runs straight away (E2).
 *   - the review shows the Vorgangs-ID, the Kundennummer, the signer names and the template
 *     reference.
 *   - step 6 Freigabe: an EMAIL mandate is released-and-sent in one action ("Freigeben & Senden");
 *     a POST/PORTAL mandate is only RELEASED here ("Freigeben"). Its delivery (Zustellen) and the
 *     Freigabe zurückziehen live on the customer screen, `MandateDeliveryActions`.
 *   - a prepare refused with 409 `mandate_process_open` points at the open mandate (onOpenMandate).
 *   - per channel (PR #66 decision 3): EMAIL shows the whole covering mail. PORTAL shows the
 *     notification text (Ansprache, Betreff, Nachricht), which the release stores for the later
 *     portal delivery. POST shows no text fields at all, only the document.
 *
 * P5-S1 ADDS for the BROKERMANDAT (targetStatus 'aktiv'):
 *   - before anything, the customer's Register-Befund is read: a locked customer opens on the lock
 *     notice instead of the signer step (the api refuses the prepare with the same 409);
 *   - after the prepare (a draft without a document): «Register-Abfrage» (firm: the Handelsregister
 *     line; both: the Sanktionsliste and PEP attestations, or «Befund» with its sources and a note)
 *     and «Mandatsumfang» (the proposal per carrier, exclusions with a reason, manual additions);
 *   - «Weiter» from the scope stores it (PUT scope) and then renders the document (POST render),
 *     once each; the review shows the Policenliste count and the P-02.S6 reference;
 *   - a Befund ends on the lock notice whose only action is «Abbrechen (Compliance)»: the draft
 *     goes, the customer stays locked until an admin clears it on the customer page.
 *
 * HOW THE ROLLBACK IS ACTUALLY SAFE
 *
 * The api only writes the customer's status on SEND. While this popup is open the customer is
 * untouched, and the new status the user picked is a purely optimistic value held by the caller.
 * So "cancel = roll back to the original status" is not a compensating write that could go wrong.
 *
 * [Source: 2026-08-11_stefan-feedback-build-plan.md] (E2, E6, E7)
 * [Source: docs/product/p4-p6-slices/p4-S1-trigger-vorgang-dokument-freigabe-design.md]
 */

import { PortalInvitationNote } from './PortalInvitationNote';
import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, Loader2, Paperclip, Send, ShieldAlert, X } from 'lucide-react';
import {
  MANDATE_BROKER_ERROR,
  MANDATE_SCOPE_EXCLUDE_REASON_INPUT,
  SCOPE_EXCLUDE_REASON_LABEL,
  scopeItemLabel,
  signerDisplayName,
} from '@brokly/shared';
import type {
  MandateRegisterCheckSource,
  MandateScopeExcludeReason,
  MandateScopeItemDto,
  RegisterCheckStateDto,
  ScopeCarrierGroup,
  ScopeItemInput,
} from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { apiClient } from '../../lib/api-client';
import { dateShortDe } from '../../lib/format';
import type {
  MandatePrepareResponse,
  MandateDispatchResult,
  MandateSignerProposalWire,
  MandateSignerWire,
} from '../../lib/api-client';

export interface MandateWizardDialogProps {
  subjectType: 'person' | 'firma';
  subjectId: string;
  /** The status the broker just picked; decides Analysemandat vs Brokermandat. */
  targetStatus: string;
  /** Shown in the header so the broker can see who this is going to. */
  subjectName: string;
  /** Cancel or close. The caller reverts its optimistic status here. */
  onCancel: () => void;
  /** The send succeeded: the caller refetches and lets the real status take over. */
  onSent: (result: MandateDispatchResult) => void;
  /**
   * The prepare was refused because a released or sent mandate of this kind is still open
   * (409 `mandate_process_open`): the caller closes the wizard and shows that mandate.
   */
  onOpenMandate?: (mandateId: string) => void;
  /** Brokermandat: a Register-Befund was recorded or its Vorgang aborted; the caller refreshes the flag. */
  onLockChanged?: () => void;
  /** Brokermandat: the lock notice's link to the customer page (navigate without a reload). */
  onOpenCustomer?: () => void;
}

/** The open mandate's id on a 409 `mandate_process_open` (ApiError.openMandateId), else null. */
function openMandateIdOf(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as { code?: unknown; openMandateId?: unknown };
  if (e.code !== 'mandate_process_open') return null;
  return typeof e.openMandateId === 'string' && e.openMandateId !== '' ? e.openMandateId : null;
}

type Phase =
  | 'checking'
  | 'signers'
  | 'preparing'
  | 'register'
  | 'scope'
  | 'rendering'
  | 'review'
  | 'sending'
  | 'releasing'
  | 'released'
  | 'locked'
  | 'failed';

/** The api's 409 for a customer under a Register-Befund. */
function isRegisterBefund(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === MANDATE_BROKER_ERROR.REGISTER_BEFUND;
}

/** One row of the scope step: a proposed item or a manual addition, with the broker's choice. */
interface ScopeChoice {
  key: string;
  item: MandateScopeItemDto | null;
  manual: { carrierName: string; policyNumber: string; sparteText: string } | null;
  included: boolean;
  excludedReason: MandateScopeExcludeReason;
}

/** The wire shape of a signer (the proposal's reference state is display only). */
function toSignerWire(s: MandateSignerWire): MandateSignerWire {
  return {
    source: s.source,
    personId: s.personId,
    firstName: s.firstName,
    lastName: s.lastName,
    roleText: s.roleText,
    signatureKind: s.signatureKind,
  };
}

/** The PUT scope item of a row: only the keys that carry something. */
function toScopeInput(c: ScopeChoice): ScopeItemInput {
  const exclusion = c.included ? {} : { excludedReason: c.excludedReason };
  if (c.manual !== null) {
    return {
      manual: {
        carrierName: c.manual.carrierName,
        ...(c.manual.policyNumber.trim() !== '' ? { policyNumber: c.manual.policyNumber.trim() } : {}),
        ...(c.manual.sparteText.trim() !== '' ? { sparteText: c.manual.sparteText.trim() } : {}),
      },
      included: c.included,
      ...exclusion,
    };
  }
  const item = c.item!;
  if (item.policyId !== null) {
    return {
      policyId: item.policyId,
      ...(item.relationshipId !== null ? { relationshipId: item.relationshipId } : {}),
      included: c.included,
      ...exclusion,
    };
  }
  return { relationshipId: item.relationshipId, included: c.included, ...exclusion };
}

function vertraegeLabel(n: number): string {
  return n === 1 ? 'Policenliste: 1 Vertrag' : `Policenliste: ${n} Verträge`;
}

/** German label for a mandate kind, matching the api. */
function kindLabel(kind: string): string {
  return kind === 'broker' ? 'Brokermandat' : 'Analysemandat';
}

/** "Vorname Nachname" for a signer (empty parts dropped). */
function signerName(s: MandateSignerWire): string {
  return signerDisplayName(s.firstName, s.lastName);
}

export function MandateWizardDialog({
  subjectType,
  subjectId,
  targetStatus,
  subjectName,
  onCancel,
  onSent,
  onOpenMandate,
  onLockChanged,
  onOpenCustomer,
}: MandateWizardDialogProps) {
  // The Brokermandat (target 'aktiv') runs the register check and the scope before the document.
  const isBroker = targetStatus === 'aktiv';
  const [phase, setPhase] = useState<Phase>(isBroker ? 'checking' : subjectType === 'firma' ? 'signers' : 'preparing');
  const [draft, setDraft] = useState<MandatePrepareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The released or sent mandate that blocked the prepare (409 mandate_process_open), if any.
  const [openMandateId, setOpenMandateId] = useState<string | null>(null);

  // P4-S1 step 3: the Handelsregister proposal (firma only) + the checked state per signer.
  const [proposal, setProposal] = useState<MandateSignerProposalWire | null>(null);
  const [checked, setChecked] = useState<boolean[]>([]);
  // The signers the last prepare was run with (so an Ansprache change re-prepares with them).
  const [lastSigners, setLastSigners] = useState<MandateSignerWire[] | undefined>(undefined);

  // The three editable fields. E6: the email is inline-editable before it goes out.
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [includeAttachment, setIncludeAttachment] = useState(true);
  const [previewing, setPreviewing] = useState(false);

  // Sie- or Du-form for the covering email (E7). Sie is the safe default.
  const [anrede, setAnrede] = useState<'sie' | 'du'>('sie');

  // Brokermandat step 3: the register state, the attestations or the Befund with its sources.
  const [registerState, setRegisterState] = useState<RegisterCheckStateDto | null>(null);
  const [registerResult, setRegisterResult] = useState<'ohne_befund' | 'befund'>('ohne_befund');
  const [attested, setAttested] = useState<{ sanktionsliste: boolean; pep: boolean }>({ sanktionsliste: false, pep: false });
  const [befundSources, setBefundSources] = useState<MandateRegisterCheckSource[]>([]);
  const [registerNote, setRegisterNote] = useState('');
  // The draft a Befund locked (the «Abbrechen (Compliance)» target); null when no draft of this wizard exists.
  const [lockDraftId, setLockDraftId] = useState<string | null>(null);

  // Brokermandat step 5: the proposal per carrier, the broker's choices, a manual addition form.
  const [scopeGroups, setScopeGroups] = useState<ScopeCarrierGroup[]>([]);
  const [choices, setChoices] = useState<ScopeChoice[]>([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState({ carrierName: '', policyNumber: '', sparteText: '' });
  const [includedCount, setIncludedCount] = useState<number | null>(null);

  const enterRegisterStep = (mandateId: string) => {
    setPhase('register');
    setRegisterState(null);
    setRegisterResult('ohne_befund');
    setAttested({ sanktionsliste: false, pep: false });
    setBefundSources([]);
    setRegisterNote('');
    apiClient.mandateBroker
      .registerCheck(mandateId)
      .then(setRegisterState)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Die Register-Abfrage konnte nicht geladen werden.');
      });
  };

  // Run prepare with the given signers + anrede, and land in review. Orphaning a previous draft
  // is invisible to the customer and harmless (same design as Abbrechen).
  const runPrepare = (signers: MandateSignerWire[] | undefined, anredeArg: 'sie' | 'du') => {
    setLastSigners(signers);
    setPhase('preparing');
    setError(null);
    setOpenMandateId(null);
    apiClient.mandateWizard
      .prepare({ subjectType, subjectId, targetStatus, anrede: anredeArg, ...(signers ? { signers } : {}) })
      .then((res) => {
        setDraft(res);
        setTo(res.email.to.join(', '));
        setSubject(res.email.subject);
        setBody(res.email.body);
        setIncludeAttachment(true);
        if (res.renderPending === true) {
          enterRegisterStep(res.mandateId);
        } else {
          setPhase('review');
        }
      })
      .catch((err: unknown) => {
        if (isRegisterBefund(err)) {
          setLockDraftId(null);
          setPhase('locked');
          return;
        }
        setError(err instanceof Error ? err.message : 'Das Dokument konnte nicht erstellt werden.');
        setOpenMandateId(openMandateIdOf(err));
        setPhase('failed');
      });
  };

  // Brokermandat: show a rendered draft in the review (the render also composes the email).
  const showRendered = (res: MandatePrepareResponse) => {
    setDraft(res);
    setTo(res.email.to.join(', '));
    setSubject(res.email.subject);
    setBody(res.email.body);
    setIncludeAttachment(true);
    setPhase('review');
  };

  // The Handelsregister counts as checked only when the register state is verified; an unverified
  // firm is attested on the two lists alone.
  const registerSources = (): MandateRegisterCheckSource[] =>
    subjectType === 'firma' && registerState?.registerStatus === 'verifiziert'
      ? ['handelsregister', 'sanktionsliste', 'pep']
      : ['sanktionsliste', 'pep'];

  const registerReady =
    registerState !== null &&
    (registerResult === 'ohne_befund' ? attested.sanktionsliste && attested.pep : befundSources.length > 0);

  const handleRegisterWeiter = async () => {
    if (!draft || !registerReady) return;
    setError(null);
    try {
      const body =
        registerResult === 'befund'
          ? {
              result: 'befund' as const,
              sources: registerSources(),
              befundSources,
              ...(registerNote.trim() !== '' ? { notes: registerNote.trim() } : {}),
            }
          : { result: 'ohne_befund' as const, sources: registerSources() };
      const check = await apiClient.mandateBroker.recordRegisterCheck(draft.mandateId, body);
      if (check.result === 'befund') {
        setLockDraftId(draft.mandateId);
        setPhase('locked');
        onLockChanged?.();
        return;
      }
      setPhase('scope');
      const proposal = await apiClient.mandateBroker.scopeProposal(draft.mandateId);
      setScopeGroups(proposal.groups);
      setChoices(
        proposal.groups.flatMap((g) =>
          g.items.map((item) => ({
            key: item.id,
            item,
            manual: null,
            included: item.included,
            excludedReason: item.excludedReason ?? 'broker',
          })),
        ),
      );
    } catch (err: unknown) {
      if (isRegisterBefund(err)) {
        setLockDraftId(draft.mandateId);
        setPhase('locked');
        onLockChanged?.();
        return;
      }
      setError(err instanceof Error ? err.message : 'Die Register-Abfrage konnte nicht gespeichert werden.');
    }
  };

  const updateChoice = (key: string, patch: Partial<ScopeChoice>) =>
    setChoices((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const addManual = () => {
    const carrierName = manualDraft.carrierName.trim();
    if (carrierName === '') return;
    setChoices((cs) => [
      ...cs,
      { key: `manual:${cs.length}`, item: null, manual: { ...manualDraft, carrierName }, included: true, excludedReason: 'broker' },
    ]);
    setManualDraft({ carrierName: '', policyNumber: '', sparteText: '' });
    setManualOpen(false);
  };

  const handleScopeWeiter = async () => {
    if (!draft) return;
    setPhase('rendering');
    setError(null);
    try {
      const stored = await apiClient.mandateBroker.setScope(draft.mandateId, { items: choices.map(toScopeInput) });
      setIncludedCount(stored.includedCount);
      // A manual addition is now a stored declared insurance: a second «Weiter» (after a failed
      // render) sends its id instead of creating the insurance again.
      setChoices((cs) => [
        ...cs.filter((c) => c.manual === null),
        ...stored.items
          .filter((i) => i.source === 'manual' && i.included)
          .map((i) => ({ key: i.id, item: i, manual: null, included: true, excludedReason: 'broker' as MandateScopeExcludeReason })),
      ]);
      const rendered = await apiClient.mandateBroker.render(draft.mandateId, { anrede });
      showRendered(rendered);
    } catch (err: unknown) {
      if (isRegisterBefund(err)) {
        setLockDraftId(draft.mandateId);
        setPhase('locked');
        return;
      }
      setError(err instanceof Error ? err.message : 'Das Dokument konnte nicht erstellt werden.');
      setPhase('scope');
    }
  };

  const handleComplianceCancel = () => {
    const id = lockDraftId;
    // Best effort, like Abbrechen: the customer flag and the Compliance task still offer the abort.
    const done = () => {
      onLockChanged?.();
      onCancel();
    };
    if (id === null) {
      done();
      return;
    }
    apiClient.mandateWizard.cancel(id, { reason: 'compliance' }).then(done, done);
  };

  // The subject + target a person's draft was prepared for, so StrictMode's second effect run does
  // not prepare again: that second prepare would hit the open-mandate check and fail with 409.
  const preparedFor = useRef<string | null>(null);

  // On open: a firma fetches the signatory proposal first (step 3); a person prepares straight away.
  // The Brokermandat first reads the customer's Register-Befund: a locked customer gets the notice.
  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (subjectType === 'firma') {
        setPhase('signers');
        setError(null);
        apiClient.mandateWizard
          .signerProposal('firma', subjectId)
          .then((p) => {
            if (cancelled) return;
            setProposal(p);
            setChecked(p.proposal.map(() => true)); // pre-checked
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            setError(err instanceof Error ? err.message : 'Die Zeichnungsberechtigten konnten nicht geladen werden.');
            setPhase('failed');
          });
      } else if (preparedFor.current !== `${subjectId}:${targetStatus}`) {
        preparedFor.current = `${subjectId}:${targetStatus}`;
        runPrepare(undefined, 'sie');
      }
    };
    if (isBroker) {
      setPhase('checking');
      apiClient.mandateBroker
        .customerRegisterBefund(subjectType, subjectId)
        .then((state) => {
          if (cancelled) return;
          if (state.befund !== null) {
            setLockDraftId(null);
            setPhase('locked');
            return;
          }
          start();
        })
        // The read failed: go on; the api refuses a locked prepare with the same 409 anyway.
        .catch(() => {
          if (!cancelled) start();
        });
    } else {
      start();
    }
    return () => {
      cancelled = true;
    };
  }, [subjectType, subjectId, targetStatus]);

  const handleWeiter = () => {
    if (proposal === null) return;
    const selected = proposal.proposal.filter((_, i) => checked[i]);
    // Prevent unchecking every signer from falling back to the full register set.
    if (proposal.proposal.length > 0 && selected.length === 0) return;
    runPrepare(selected.length > 0 ? selected.map(toSignerWire) : undefined, anrede);
  };

  const handleAnrede = async (a: 'sie' | 'du') => {
    setAnrede(a);
    // Brokermandat: the register check and the scope belong to this draft; render it again with
    // the other form of address instead of starting over.
    if (draft !== null && draft.mandateKind === 'broker') {
      setPhase('rendering');
      setError(null);
      try {
        showRendered(await apiClient.mandateBroker.render(draft.mandateId, { anrede: a }));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Das Dokument konnte nicht erstellt werden.');
        setPhase('review');
      }
      return;
    }
    // Drop the open draft explicitly; the api would also replace it on the new prepare.
    const id = draft?.mandateId;
    if (id) await apiClient.mandateWizard.cancel(id).catch(() => undefined);
    runPrepare(lastSigners, a);
  };

  const handleCancel = () => {
    const id = draft?.mandateId;
    // Closing in phase 'released' must keep the released mandate intact for delivery.
    // Best-effort cleanup: even if the cancel call fails, the modal still closes so the user is never trapped.
    if (id && phase !== 'released') void apiClient.mandateWizard.cancel(id).catch(() => undefined);
    onCancel();
  };

  const handlePreview = async () => {
    if (!draft || draft.documentId === null) return;
    const documentId = draft.documentId;
    setPreviewing(true);
    setError(null);
    try {
      // Fetched via apiClient (bearer token); window.open on a raw API URL would 401.
      const blob = await apiClient.mandateWizard.documentBlob(draft.mandateId, documentId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after 60s to allow the browser tab enough time to load the PDF without leaking memory.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Das PDF konnte nicht geladen werden.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!draft) return;
    const recipients = to
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    if (recipients.length === 0) {
      setError('Bitte erfassen Sie mindestens eine Empfängeradresse.');
      return;
    }
    setPhase('sending');
    setError(null);
    try {
      const result = await apiClient.mandateWizard.send(draft.mandateId, {
        to: recipients,
        subject,
        body,
        includeAttachment,
      });
      onSent(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Der Versand ist fehlgeschlagen.');
      // Keep dialog open in review phase with composed email text intact so user does not lose edits.
      setPhase('review');
    }
  };

  // P4-S1 step 6: a POST/PORTAL mandate is only RELEASED here; delivery follows in P4-S2.
  // PORTAL: the reviewed notification goes with the release, so the edits are not lost.
  const handleRelease = async () => {
    if (!draft) return;
    const portal = draft.deliveryChannel === 'portal';
    if (portal && (subject.trim() === '' || body.trim() === '')) {
      setError('Bitte erfassen Sie Betreff und Nachricht der Benachrichtigung.');
      return;
    }
    setPhase('releasing');
    setError(null);
    try {
      if (portal) {
        await apiClient.mandateWizard.release(draft.mandateId, { notification: { subject, body, anrede } });
      } else {
        await apiClient.mandateWizard.release(draft.mandateId);
      }
      setPhase('released');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Die Freigabe ist fehlgeschlagen.');
      setPhase('review');
    }
  };

  const busy = phase === 'preparing' || phase === 'sending' || phase === 'releasing' || phase === 'rendering' || phase === 'checking';
  const showReview = draft !== null && (phase === 'review' || phase === 'sending' || phase === 'releasing');
  const label = draft ? kindLabel(draft.mandateKind) : 'Mandat';
  // EMAIL: release-and-send in one action; POST/PORTAL: release only (delivery = P4-S2).
  const isEmail = draft?.deliveryChannel === 'email';
  const isPortal = draft?.deliveryChannel === 'portal';
  // Sie/Du and the text fields only exist where a text goes out: the email, or the portal
  // notification. A post mandate is the document alone.
  const hasText = isEmail || isPortal;
  const primaryLabel = isEmail ? 'Freigeben & Senden' : 'Freigeben';

  // No backdrop click-to-close: prevents accidental dismissal during email review and composing.
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`${label} versenden`}
    >
      <div className="mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold">{label} versenden</h2>
            <p className="text-xs text-muted-foreground">
              an {subjectName} · Status wird nach dem Versand auf {targetStatus} gesetzt
            </p>
          </div>
          {/* A Befund leaves one way out: «Abbrechen (Compliance)». Without a draft the dialog just closes. */}
          {!(phase === 'locked' && lockDraftId !== null) && (
            <button
              type="button"
              onClick={phase === 'locked' ? handleComplianceCancel : handleCancel}
              disabled={busy}
              aria-label="Schliessen"
              className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {(phase === 'preparing' || phase === 'rendering' || phase === 'checking') && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {phase === 'checking' ? 'Kunde wird geprüft …' : 'Dokument wird erstellt …'}
            </p>
          )}

          {/* Brokermandat: the lock notice of a Register-Befund. */}
          {phase === 'locked' && (
            <div className="flex flex-col gap-2 text-sm" data-testid="register-lock">
              <p className="flex items-start gap-2 text-destructive">
                <ShieldAlert className="size-4 shrink-0" />
                Register-Befund: Der Vorgang ist gesperrt.
              </p>
              <p className="text-xs text-muted-foreground">
                Ein Brokermandat ist erst möglich, wenn der Befund auf der Kundenseite mit einer
                Begründung freigegeben wurde. Die Compliance-Aufgabe liegt in den Pendenzen.
              </p>
              <a
                className="text-xs text-primary underline"
                href={`/kunden/${subjectId}`}
                onClick={(e) => {
                  // Inside the app the caller navigates (no page reload); the href stays for a new tab.
                  if (onOpenCustomer) {
                    e.preventDefault();
                    onOpenCustomer();
                  }
                }}
              >
                Zum Kundenprofil
              </a>
            </div>
          )}

          {/* Brokermandat step 3: the register check (manual attestation). */}
          {phase === 'register' && (
            <div className="flex flex-col gap-3" data-testid="register-step">
              <span className="text-xs font-medium">Register-Abfrage</span>
              {registerState === null ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Registerdaten werden geladen …
                </p>
              ) : (
                <>
                  {subjectType === 'firma' && (
                    <p className="text-xs">
                      Handelsregister: {registerState.registerStatus === 'verifiziert' ? 'verifiziert' : 'nicht verifiziert'}
                      {registerState.registerFetchedAt ? ` (Stand ${dateShortDe(registerState.registerFetchedAt)})` : ''}
                    </p>
                  )}
                  <div className="flex gap-3 text-xs" role="radiogroup" aria-label="Ergebnis">
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="register-result"
                        checked={registerResult === 'ohne_befund'}
                        onChange={() => setRegisterResult('ohne_befund')}
                        aria-label="ohne Befund"
                      />
                      ohne Befund
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="register-result"
                        checked={registerResult === 'befund'}
                        onChange={() => setRegisterResult('befund')}
                        aria-label="Befund"
                      />
                      Befund
                    </label>
                  </div>
                  {registerResult === 'ohne_befund' ? (
                    <div className="flex flex-col gap-1 text-sm">
                      {(['sanktionsliste', 'pep'] as const).map((src) => (
                        <label key={src} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={attested[src]}
                            onChange={(e) => setAttested((a) => ({ ...a, [src]: e.target.checked }))}
                            aria-label={`${src === 'pep' ? 'PEP' : 'Sanktionsliste'}: geprüft, ohne Befund`}
                          />
                          {src === 'pep' ? 'PEP' : 'Sanktionsliste'}: geprüft, ohne Befund
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 text-sm">
                      {registerSources().map((src) => (
                        <label key={src} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={befundSources.includes(src)}
                            onChange={(e) =>
                              setBefundSources((b) => (e.target.checked ? [...b, src] : b.filter((x) => x !== src)))
                            }
                            aria-label={`Befund in: ${src === 'pep' ? 'PEP' : src === 'handelsregister' ? 'Handelsregister' : 'Sanktionsliste'}`}
                          />
                          Befund in: {src === 'pep' ? 'PEP' : src === 'handelsregister' ? 'Handelsregister' : 'Sanktionsliste'}
                        </label>
                      ))}
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium">Notiz</span>
                        <textarea
                          className="min-h-16 rounded border bg-background px-2 py-1.5 text-sm"
                          value={registerNote}
                          maxLength={2000}
                          onChange={(e) => setRegisterNote(e.target.value)}
                          aria-label="Notiz"
                        />
                      </label>
                    </div>
                  )}
                </>
              )}
              {error !== null && <p className="text-xs text-destructive">{error}</p>}
            </div>
          )}

          {/* Brokermandat step 5: the Mandatsumfang. */}
          {(phase === 'scope' || (phase === 'rendering' && choices.length > 0 && draft?.renderPending === true)) && (
            <div className="flex flex-col gap-3" data-testid="scope-step">
              <span className="text-xs font-medium">Mandatsumfang</span>
              {scopeGroups.map((g) => (
                <div key={g.carrierKey} className="flex flex-col gap-1 rounded border px-3 py-2">
                  <span className="text-sm font-medium">{g.carrierName ?? g.carrierUid}</span>
                  {choices
                    .filter((c) => c.item !== null && g.items.some((i) => i.id === c.key))
                    .map((c) => {
                      const label = scopeItemLabel(c.item!);
                      return (
                        <div key={c.key} className="flex flex-wrap items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={c.included}
                            onChange={(e) => updateChoice(c.key, { included: e.target.checked })}
                            aria-label={label}
                          />
                          <span className="flex-1">
                            {c.item!.sparteText ? `${c.item!.sparteText} · ` : ''}
                            {c.item!.policyNumber ? `Police Nr. ${c.item!.policyNumber}` : 'ohne Policennummer'}
                          </span>
                          {c.item!.draftPolicy === true && <span className="text-xs text-amber-700">Entwurf</span>}
                          {!c.included && (
                            <select
                              className="rounded border bg-background px-1 py-0.5 text-xs"
                              value={c.excludedReason}
                              onChange={(e) => updateChoice(c.key, { excludedReason: e.target.value as MandateScopeExcludeReason })}
                              aria-label={`Grund für ${label}`}
                            >
                              {MANDATE_SCOPE_EXCLUDE_REASON_INPUT.map((r) => (
                                <option key={r} value={r}>
                                  {SCOPE_EXCLUDE_REASON_LABEL[r]}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })}
                </div>
              ))}
              {choices.some((c) => c.manual !== null || c.item?.source === 'manual') && (
                <ul className="flex flex-col gap-1 rounded border px-3 py-2 text-sm">
                  {choices
                    .filter((c) => c.manual !== null || c.item?.source === 'manual')
                    .map((c) => {
                      const name = c.manual?.carrierName ?? c.item?.carrierName ?? '';
                      const number = (c.manual?.policyNumber ?? c.item?.policyNumber ?? '').trim();
                      return (
                        <li key={c.key} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={c.included}
                            onChange={(e) =>
                              c.manual !== null
                                ? // Not stored yet: unticking removes the addition.
                                  setChoices((cs) => cs.filter((x) => x.key !== c.key))
                                : updateChoice(c.key, { included: e.target.checked })
                            }
                            aria-label={`${name} · ${number !== '' ? `Police Nr. ${number}` : 'ohne Policennummer'}`}
                          />
                          <span className="font-medium">{name}</span>
                          <span className="text-muted-foreground">
                            {number !== '' ? `Police Nr. ${number}` : 'ohne Policennummer'}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              )}
              {manualOpen ? (
                <div className="flex flex-col gap-2 rounded border px-3 py-2 text-xs">
                  <label className="flex flex-col gap-1">
                    <span>Versicherer</span>
                    <input
                      className="rounded border bg-background px-2 py-1 text-sm"
                      value={manualDraft.carrierName}
                      maxLength={200}
                      onChange={(e) => setManualDraft((m) => ({ ...m, carrierName: e.target.value }))}
                      aria-label="Versicherer"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span>Sparte</span>
                    <input
                      className="rounded border bg-background px-2 py-1 text-sm"
                      value={manualDraft.sparteText}
                      maxLength={200}
                      onChange={(e) => setManualDraft((m) => ({ ...m, sparteText: e.target.value }))}
                      aria-label="Sparte"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span>Policennummer</span>
                    <input
                      className="rounded border bg-background px-2 py-1 text-sm"
                      value={manualDraft.policyNumber}
                      maxLength={100}
                      onChange={(e) => setManualDraft((m) => ({ ...m, policyNumber: e.target.value }))}
                      aria-label="Policennummer"
                    />
                  </label>
                  <div>
                    <Button size="sm" variant="outline" onClick={addManual} disabled={manualDraft.carrierName.trim() === ''}>
                      Hinzufügen
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Button size="sm" variant="outline" onClick={() => setManualOpen(true)}>
                    Police manuell ergänzen
                  </Button>
                </div>
              )}
              {error !== null && <p className="text-xs text-destructive">{error}</p>}
            </div>
          )}

          {phase === 'failed' && (
            <p className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="size-4 shrink-0" />
              {error}
            </p>
          )}

          {/* A released or sent mandate of this kind blocks a new one: point at it, no dead end. */}
          {phase === 'failed' && openMandateId !== null && (
            <div className="mt-3 flex flex-col items-start gap-2 text-xs text-muted-foreground" data-testid="open-mandate-hint">
              <p>
                Das offene Mandat finden Sie in der Kundenübersicht. Ist es freigegeben, aber noch nicht
                zugestellt: Zustellen oder Freigabe zurückziehen, danach können Sie neu starten.
              </p>
              {onOpenMandate && (
                <Button size="sm" variant="outline" onClick={() => onOpenMandate(openMandateId)}>
                  Offenes Mandat anzeigen
                </Button>
              )}
            </div>
          )}

          {phase === 'released' && (
            <p className="flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="size-4 shrink-0" />
              Freigegeben, Zustellung folgt
            </p>
          )}
          {phase === 'released' && (
            <p className="mt-1 text-xs text-muted-foreground">
              Zustellen oder die Freigabe zurückziehen können Sie beim Mandat in der Kundenübersicht.
            </p>
          )}

          {/* P4-S1 step 3: the Unterzeichnende proposal (firma only). */}
          {phase === 'signers' && (
            <div className="flex flex-col gap-3" data-testid="signer-step">
              <div className="text-xs">
                <span className="font-medium">Unterzeichnende Personen</span>
                {proposal !== null && (
                  <span className="ml-2 text-muted-foreground">
                    Handelsregister: {proposal.registerStatus}
                  </span>
                )}
              </div>
              {proposal === null ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Zeichnungsberechtigte werden geladen …
                </p>
              ) : proposal.proposal.length === 0 ? (
                <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
                  Keine Zeichnungsberechtigten im Handelsregister (nicht verifiziert)
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {proposal.proposal.map((s, i) => (
                    <li key={i} className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked[i] ?? false}
                        onChange={(e) => setChecked((c) => c.map((v, j) => (j === i ? e.target.checked : v)))}
                        aria-label={`Unterzeichnende/r ${signerName(s)}`}
                      />
                      <span className="flex-1">
                        <span className="font-medium">{signerName(s)}</span>
                        {s.roleText ? <span className="text-muted-foreground"> · {s.roleText}</span> : null}
                        {s.signatureKind ? <span className="text-muted-foreground"> · {s.signatureKind}</span> : null}
                        {s.referenceStatus === 'bestaetigt' ? (
                          <span className="text-emerald-700 dark:text-emerald-300"> · Referenz aus P4 vorhanden</span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {showReview && (
            <div className="flex flex-col gap-4">
              {/* P4-S1 review header: Vorgangs-ID + Kundennummer + signers + template reference. */}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded border bg-muted/40 px-3 py-2 text-xs" data-testid="vorgang-summary">
                <dt className="text-muted-foreground">Vorgangs-ID</dt>
                <dd className="mono-num">{draft.processNumber}</dd>
                <dt className="text-muted-foreground">Kundennummer</dt>
                <dd className="mono-num">{draft.customerNumber ?? '-'}</dd>
                <dt className="text-muted-foreground">Unterzeichnende</dt>
                <dd>{draft.signers.map(signerName).filter((n) => n !== '').join(', ') || '-'}</dd>
                <dt className="text-muted-foreground">Textvorlage</dt>
                <dd className="mono-num">{draft.email.templateRef}</dd>
                {draft.mandateKind === 'broker' && includedCount !== null && (
                  <>
                    <dt className="text-muted-foreground">Mandatsumfang</dt>
                    <dd>{vertraegeLabel(includedCount)}</dd>
                  </>
                )}
              </dl>

              {draft.release.routing === 'automated' && (
                <p className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
                  Freigabe automatisch möglich (Schwellwert erreicht)
                </p>
              )}

              {draft.email.recipientSource === 'none' && isEmail && (
                <p className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
                  <AlertCircle className="size-4 shrink-0" />
                  Für diesen Kunden ist keine E-Mail-Adresse hinterlegt. Bitte erfassen Sie
                  eine Adresse, bevor Sie versenden.
                </p>
              )}

              {draft.mandateKind === 'broker' && <PortalInvitationNote plan={draft.invitation} />}

              {isPortal && (
                <p className="rounded border bg-muted/40 px-3 py-2 text-xs" data-testid="portal-note">
                  Benachrichtigung zur Zustellung über das Portal. Der Text wird mit der Freigabe
                  gespeichert und bei der Zustellung verwendet.
                </p>
              )}

              {draft.deliveryChannel === 'post' && (
                <p className="rounded border bg-muted/40 px-3 py-2 text-xs" data-testid="post-note">
                  Zustellung per Post: Das Dokument wird ohne Begleittext versandt.
                </p>
              )}

              {hasText && (
                <div className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">Ansprache</span>
                  <div className="flex gap-1" role="group" aria-label="Ansprache">
                    {(['sie', 'du'] as const).map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => void handleAnrede(a)}
                        disabled={busy}
                        aria-pressed={anrede === a}
                        className={`rounded border px-3 py-1.5 text-sm disabled:opacity-50 ${
                          anrede === a
                            ? 'border-primary bg-primary/10 font-medium'
                            : 'bg-background text-muted-foreground'
                        }`}
                      >
                        {a === 'sie' ? 'Sie-Form' : 'Du-Form'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isEmail && (
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">Empfänger</span>
                  <input
                    className="rounded border bg-background px-2 py-1.5 text-sm"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    disabled={busy}
                    aria-label="Empfänger"
                  />
                </label>
              )}

              {hasText && (
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">Betreff</span>
                  <input
                    className="rounded border bg-background px-2 py-1.5 text-sm"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={busy}
                    aria-label="Betreff"
                  />
                </label>
              )}

              {hasText && (
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium">Nachricht</span>
                  <textarea
                    className="min-h-40 rounded border bg-background px-2 py-1.5 font-sans text-sm"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    disabled={busy}
                    aria-label="Nachricht"
                  />
                </label>
              )}

              <div className="flex items-center justify-between rounded border px-3 py-2">
                <span className="flex items-center gap-2 text-xs">
                  <Paperclip className="size-4 text-muted-foreground" />
                  <span className={!isEmail || includeAttachment ? '' : 'text-muted-foreground line-through'}>
                    {draft.filename}
                  </span>
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void handlePreview()}
                    disabled={busy || previewing}
                    aria-label="PDF ansehen"
                    className="flex items-center gap-1 rounded px-1.5 py-1 text-xs text-primary hover:bg-muted disabled:opacity-50"
                  >
                    {previewing ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Eye className="size-3.5" />
                    )}
                    PDF ansehen
                  </button>
                  {/* Only an email carries an attachment that can be removed. */}
                  {isEmail && (
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={includeAttachment}
                        onChange={(e) => setIncludeAttachment(e.target.checked)}
                        disabled={busy}
                        aria-label="Anhang mitsenden"
                      />
                      Anhang mitsenden
                    </label>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Das Dokument trägt die Referenz {draft.barcodeToken}. Der unterzeichnete
                Rücklauf wird darüber automatisch diesem Kunden zugeordnet.
              </p>

              {error !== null && (
                <p className="flex items-start gap-2 text-xs text-destructive">
                  <AlertCircle className="size-4 shrink-0" />
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
          {phase === 'locked' ? (
            lockDraftId !== null && (
              <Button variant="outline" size="sm" onClick={handleComplianceCancel}>
                Abbrechen (Compliance)
              </Button>
            )
          ) : (
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={busy}>
              {phase === 'released' ? 'Schliessen' : 'Abbrechen'}
            </Button>
          )}

          {phase === 'register' && (
            <Button size="sm" onClick={() => void handleRegisterWeiter()} disabled={!registerReady}>
              Weiter
            </Button>
          )}

          {phase === 'scope' && (
            <Button size="sm" onClick={() => void handleScopeWeiter()} disabled={!choices.some((c) => c.included)}>
              Weiter
            </Button>
          )}

          {phase === 'signers' && (
            <Button
              size="sm"
              onClick={handleWeiter}
              disabled={proposal === null || (proposal.proposal.length > 0 && !checked.some(Boolean))}
            >
              Weiter
            </Button>
          )}

          {showReview && draft.mandateKind === 'broker' && choices.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setPhase('scope')} disabled={busy}>
              Zurück zum Mandatsumfang
            </Button>
          )}

          {showReview && (
            <Button
              size="sm"
              onClick={() => void (isEmail ? handleSend() : handleRelease())}
              disabled={busy}
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              {primaryLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
